import { Box, Button, Link, Typography } from '@mui/material';
import { Heading } from '@natomalabs/natomui/src/components/Typography';
import TextField from '@/src/components/TextField';
import { useCallback, useState } from 'react';
import useEnvironment from '../providers/Environment';
import LoginStatusForm from './LoginStatusForm';
import { LoginActionState } from '@natomalabs/common/auth';
import { useSearchParams } from 'next/navigation';
import { validateEmail } from '@natomalabs/common/strings';
import GoogleLoginButton from './GoogleLoginButton';
import GitHubLoginButton from './GitHubLoginButton';

export default function LoginFlow() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { natoma, auth } = useEnvironment();
  const [email, setEmail] = useState('');
  const [action, setAction] = useState<LoginActionState | null>(null);
  const searchParams = useSearchParams();
  const redirectUrl = searchParams.get('redirect');

  // Map OAuth error codes to friendly messages
  const ERROR_MESSAGES: Record<string, string> = {
    oauth_cancelled: 'Sign in was canceled. Please try again.',
    session_expired: 'Your session expired. Please try again.',
    invalid_request: 'Invalid request. Please sign in again.',
    auth_failed: 'Authentication failed. Please try again.'
  };
  const urlErrorParam = searchParams.get('error');
  const urlError = urlErrorParam
    ? ERROR_MESSAGES[urlErrorParam] || 'An error occurred. Please try again.'
    : null;
  const displayError = error || urlError;

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setIsSubmitting(true);
      setError(null);

      const emailCheck = validateEmail(email);
      if (!emailCheck.isValid) {
        setError(emailCheck.message);
        setIsSubmitting(false);
        return;
      }

      // Extract orgSlug from current hostname
      const orgSlug = window.location.hostname
        .replace(`.${natoma.rootDomain}`, '')
        .replace(natoma.rootDomain, '');

      try {
        const response = await fetch(
          `https://api.${natoma.rootDomain}/auth/login`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              email,
              orgSlug: orgSlug || undefined,
              redirectUrl: redirectUrl || undefined
            })
          }
        );

        if (response.ok) {
          const data = await response.json();
          setAction(data.action);
          if (data.redirectUrl) {
            window.location.href = data.redirectUrl;
          }
          return;
        } else {
          // Handle error response
          const errorData = await response.json();
          setError(errorData.error || 'Login failed');
        }
      } catch (err) {
        setError('Network error occurred');
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, natoma.rootDomain, redirectUrl]
  );

  if (action === LoginActionState.MAGIC_LINK) {
    return (
      <LoginStatusForm
        title="Verify your email…"
        message={
          <>
            We&apos;ve sent a verification link to {email}. Click the link in
            the email to continue.
          </>
        }
      />
    );
  }

  if (action === LoginActionState.SAML_REDIRECT) {
    return (
      <LoginStatusForm
        title="Redirecting to SAML login…"
        message={<>You are being redirected to the SAML login page.</>}
      />
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: theme.spacing(50)
      })}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Heading>Welcome to Natoma</Heading>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <TextField
            fullWidth
            placeholder="Email address"
            name="email"
            type="email"
            required
            value={email}
            autoFocus
            onChange={(e) => setEmail(e.target.value)}
          />
          {displayError && (
            <Typography color="error" variant="base">
              {displayError}
            </Typography>
          )}
        </Box>
        <Button
          fullWidth
          variant="primary"
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Sending...' : 'Continue'}
        </Button>
      </Box>
      {!auth.disableSocialLogin && (
        <>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="base" color="text.secondary">
              Or
            </Typography>
          </Box>

          <GoogleLoginButton redirectUrl={redirectUrl} />
          <GitHubLoginButton redirectUrl={redirectUrl} />
        </>
      )}
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body" sx={{ color: 'text.secondary' }}>
          By continuing, you agree to our
          <br />
          <Link
            target="_blank"
            href="https://natoma.ai/terms-of-service"
            sx={{ textDecoration: 'underline' }}
          >
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link
            target="_blank"
            href="https://natoma.ai/privacy-policy"
            sx={{ textDecoration: 'underline' }}
          >
            Privacy Policy
          </Link>
          .
        </Typography>
      </Box>
    </Box>
  );
}
